import { useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Accordion, Button, Text, useModalControl, Avatar } from '@frontend/design-system';
import { useAvailabilityData } from '../../contexts/availability-data-context';
import type { AvailabilityRule, OperatoryWithAvailabilityRules } from '../../types/availability-rules.types';
import { AvailabilityTable } from './availability-table';
import { AvailabilityTray } from './availability-tray';
import { DeleteAvailabilityModal } from './delete-availability-modal';
import { useAvailabilityForm } from './use-availability-form';

type OperatoryContainerProps = {
  operatory: OperatoryWithAvailabilityRules;
};

export const OperatoryContainer = ({ operatory }: OperatoryContainerProps) => {
  const { t } = useTranslation('schedule-settings');
  const { locationId, refetchAvailabilityRulesData, setHasUnpublishedChanges } = useAvailabilityData();
  const { selectedOrgId } = useAppScopeStore();

  const { isSmallMode } = useIsSmallMode();
  const [editingTemplate, setEditingTemplate] = useState<AvailabilityRule | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<AvailabilityRule | null>(null);

  const { modalProps, triggerProps } = useModalControl();
  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const { deleteAvailability, isSaving } = useAvailabilityForm({
    locationId,
    orgId: selectedOrgId,
    onSuccess: () => {
      refetchAvailabilityRulesData();
      setHasUnpublishedChanges(true);
      setDeletingTemplate(null);
      deleteModalProps.onClose();
    },
  });

  const handleEdit = (template: AvailabilityRule) => {
    setEditingTemplate(template);
    triggerProps.onClick?.();
  };

  const handleAddNew = () => {
    setEditingTemplate(null);
    triggerProps.onClick?.();
  };

  const handleModalClose = () => {
    setEditingTemplate(null);
    modalProps.onClose();
  };

  const handleDelete = (template: AvailabilityRule) => {
    setDeletingTemplate(template);
    deleteTriggerProps.onClick?.();
  };

  const handleConfirmDelete = () => {
    if (deletingTemplate) {
      deleteAvailability(deletingTemplate);
    }
  };

  const count = operatory.availabilityRules ? operatory.availabilityRules.length : 0;
  if (!operatory) return null;

  return (
    <section css={containerStyles}>
      <header css={headerStyles(isSmallMode, !!operatory.name)}>
        <div css={avatarColumnStyles}>
          <Avatar name={operatory.name || 'Operatory'} size='large' />
        </div>
        <div
          css={{
            gridColumn: '2',
            gridRow: '1',
            display: 'flex',
            flexDirection: isSmallMode ? 'column' : 'row',
            gap: theme.spacing(1),
          }}
        >
          <Text>{operatory.name}</Text>
        </div>
      </header>
      {operatory.availabilityRules.length ? (
        <Accordion variant='blank'>
          <Accordion.Item value='schedule definition templates' css={{ borderRadius: theme.borderRadius.medium }}>
            <Accordion.Header>
              <Text>{t('Availability')}</Text>
              <Text css={{ marginLeft: theme.spacing(1), color: theme.colors.neutral60 }}>{count}</Text>
            </Accordion.Header>
            <Accordion.Body css={{ padding: 0 }}>
              <AvailabilityTable
                onEdit={handleEdit}
                onDelete={handleDelete}
                availabilityRules={operatory.availabilityRules}
              />
              <Button
                variant='tertiary'
                iconName='plus-small'
                css={{ margin: theme.spacing(1.5) }}
                onClick={handleAddNew}
              >
                {t('Add Availability')}
              </Button>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      ) : (
        <Button variant='tertiary' iconName='plus-small' css={{ margin: theme.spacing(1.5) }} onClick={handleAddNew}>
          {t('Add Availability')}
        </Button>
      )}
      {modalProps.show && (
        <AvailabilityTray
          key={editingTemplate?.id ?? ''}
          modalProps={{ ...modalProps, onClose: handleModalClose }}
          operatory={operatory}
          locationId={locationId}
          initialData={editingTemplate}
        />
      )}
      {deleteModalProps.show && deletingTemplate && (
        <DeleteAvailabilityModal
          modalProps={deleteModalProps}
          rule={deletingTemplate}
          operatoryName={operatory.name || ''}
          onConfirm={handleConfirmDelete}
          isDeleting={isSaving}
        />
      )}
    </section>
  );
};

const containerStyles = {
  border: `1px solid ${theme.colors.neutral30}`,
  borderRadius: theme.borderRadius.medium,
};

const headerStyles = (isSmallMode: boolean, hasDisplayName: boolean) =>
  css({
    display: 'grid',
    gridTemplateColumns: 'minmax(32px, 64px) auto',
    gridTemplateRows: hasDisplayName && isSmallMode ? 'auto auto' : 'auto',
    justifyContent: 'start',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.colors.neutral20}`,
  });

const avatarColumnStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gridRow: '1 / -1',
};
